import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { itemsAPI, settingsAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiPackage } from 'react-icons/fi';
import './Items.css';

const Items = () => {
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    unitPrice: '',
    unit: 'pcs',
    taxRate: '5',
    taxName: 'VAT',
    discountable: true,
    category: '',
  });

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchUnits();
  }, [pagination.page, category]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await itemsAPI.getAll({
        search,
        category,
        page: pagination.page,
        limit: pagination.limit,
      });
      setItems(response.data.items);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await itemsAPI.getCategories();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await settingsAPI.getUnits();
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Failed to load units');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchItems();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const openCreateModal = () => {
    setEditingItem(null);
    // Set default unit from fetched units or fallback to 'pcs'
    const defaultUnit = units.length > 0 
      ? (units.find(u => u.is_active)?.abbreviation || units.find(u => u.is_active)?.name || 'pcs')
      : 'pcs';
    setFormData({
      sku: '',
      name: '',
      description: '',
      unitPrice: '',
      unit: defaultUnit,
      taxRate: '5',
      taxName: 'VAT',
      discountable: true,
      category: '',
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      sku: item.sku || '',
      name: item.name,
      description: item.description || '',
      unitPrice: item.unit_price.toString(),
      unit: item.unit || 'pcs',
      taxRate: item.tax_rate?.toString() || '0',
      taxName: item.tax_name || 'VAT',
      discountable: item.discountable,
      category: item.category || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice),
        taxRate: parseFloat(formData.taxRate),
      };

      if (editingItem) {
        await itemsAPI.update(editingItem.id, data);
        toast.success('Item updated successfully');
      } else {
        await itemsAPI.create(data);
        toast.success('Item created successfully');
      }
      setShowModal(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (item) => {
    const confirmed = await confirm({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await itemsAPI.delete(item.id);
      if (response.data.deactivated) {
        toast.info('Item deactivated (used in existing invoices)');
      } else {
        toast.success('Item deleted successfully');
      }
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete item');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
    }).format(amount || 0);
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Items</h1>
          <p>Manage your product/service catalog</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FiPlus /> Add Item
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="search-bar">
            <div className="search-input">
              <FiSearch className="search-input-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-control"
              style={{ width: 'auto' }}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <FiPackage className="empty-state-icon" />
              <h3 className="empty-state-title">No items found</h3>
              <p className="empty-state-text">
                {search ? 'Try adjusting your search criteria' : 'Get started by adding your first item'}
              </p>
              {!search && (
                <button className="btn btn-primary" onClick={openCreateModal}>
                  <FiPlus /> Add Item
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Unit Price</th>
                      <th>Tax</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.sku || '-'}</td>
                        <td>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {item.description && (
                              <div className="text-muted text-sm">{item.description.substring(0, 50)}...</div>
                            )}
                          </div>
                        </td>
                        <td>{item.category || '-'}</td>
                        <td>{formatCurrency(item.unit_price)} / {item.unit}</td>
                        <td>{item.tax_rate}% {item.tax_name}</td>
                        <td>
                          <span className={`badge ${item.is_active ? 'badge-paid' : 'badge-cancelled'}`}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openEditModal(item)}
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(item)}
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Item Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">SKU</label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Optional"
                      list="categories-list"
                    />
                    <datalist id="categories-list">
                      {categories.map(cat => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label required">Item Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="form-control"
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Unit Price</label>
                    <input
                      type="number"
                      name="unitPrice"
                      value={formData.unitPrice}
                      onChange={handleChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {units.length > 0 ? (
                        units.filter(u => u.is_active).map(unit => (
                          <option key={unit.id} value={unit.abbreviation || unit.name}>
                            {unit.name} {unit.abbreviation ? `(${unit.abbreviation})` : ''}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="pcs">Pieces (pcs)</option>
                          <option value="hrs">Hours (hrs)</option>
                          <option value="days">Days</option>
                          <option value="kg">Kilograms (kg)</option>
                          <option value="m">Meters (m)</option>
                          <option value="l">Liters (l)</option>
                          <option value="box">Box</option>
                          <option value="set">Set</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tax Rate (%)</label>
                    <input
                      type="number"
                      name="taxRate"
                      value={formData.taxRate}
                      onChange={handleChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tax Name</label>
                    <input
                      type="text"
                      name="taxName"
                      value={formData.taxName}
                      onChange={handleChange}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="discountable"
                      checked={formData.discountable}
                      onChange={handleChange}
                      id="discountable"
                    />
                    <label htmlFor="discountable">Allow discounts on this item</label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update Item' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Items;